import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { btnSx } from "../components/ActionDrawer";
import DashTable, { Oid, PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const inr = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/* AC3 reconcile, AC4 clear balance. Payment fields only. */
const Receipts = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchReceipts(token);
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching receipts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toItem = (row, id, screenKey) => ({
    id,
    screenKey,
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    clinicName: row.clinicName,
    context: {
      amount: row.amount,
      receivedOn: shortDate(row.receivedAt),
      reference: row.reference,
      mode: row.mode,
      orderTotal: row.orderTotal,
      advanceReceived: row.advanceReceived,
      balanceReceived: row.balanceReceived,
      outstanding: row.outstanding,
    },
  });

  const columns = [
    { label: "Receipt" },
    { label: "Order" },
    { label: "Clinic" },
    { label: "Type" },
    { label: "Amount", num: true },
    { label: "Received" },
    { label: "Books status" },
    { label: "Action" },
  ];

  const tableRows = rows.map((row) => ({
    id: row.orderId,
    key: row.receiptNumber || `${row.orderId}-${row.type}`,
    cells: [
      <Oid>{row.receiptNumber || "—"}</Oid>,
      row.orderNumber,
      row.clinicName,
      row.type === "balance" ? "Balance" : "Advance",
      inr(row.amount),
      shortDate(row.receivedAt),
      row.reconciled ? (
        <Tag tone="g">Reconciled</Tag>
      ) : (
        <Tag tone="s">Posted, not reconciled</Tag>
      ),
      !row.reconciled ? (
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setActive(toItem(row, "AC3", "reconcile"));
          }}
          sx={btnSx("ghost")}
        >
          Reconcile
        </Box>
      ) : row.type === "balance" && !row.clearedForDispatch ? (
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setActive(toItem(row, "AC4", "clear-balance"));
          }}
          sx={btnSx("ghost")}
        >
          Clear for dispatch
        </Box>
      ) : (
        "—"
      ),
    ],
  }));

  const kpis = summary
    ? [
        { label: "Collected", value: inr(summary.collected) },
        { label: "Unreconciled", value: inr(summary.unreconciled), alarm: true },
        { label: "Receipts", value: summary.count ?? rows.length },
        { label: "Awaiting clearance", value: summary.awaitingClearance },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Receipts"
        lede="Write access is limited to payment fields. You cannot edit order or clinical data."
        kpis={kpis}
      />

      <DashTable
        columns={columns}
        rows={tableRows}
        onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
        emptyText="No receipts yet."
        errorText={loadFailed ? "Could not load receipts." : null}
        onRetry={fetchRows}
        foot={
          summary
            ? `${summary.count ?? rows.length} receipts · ${inr(
                summary.collected,
              )} collected`
            : null
        }
      />

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Receipts;
